/**
 * api/nav.ts —— 导航能力
 *
 * 切换底部三个页面。看起来简单，但它是 IL 能「操控界面」最直接的证据 ——
 * 用户说「打开联系人」而应用真的翻页，这是整个 PoC 想证明的事。
 *
 * 这里有一个**有意为之的不对称**：
 *   nav.go() 只切换页面，不打开任何覆盖层。
 *   打开会话、打开表单这类动作归各自的域（如 session.open()），
 *   因为它们需要参数（哪个会话？），语义也更具体。
 *   让 nav 只管「三个 tab」，AI 才不容易混用。
 */

import { uiStore } from '@/core/ui-store';
import { settingsRepo } from '@/settings/settings';
import { eventBus } from '@/core/event-bus';
import type { TabKey } from '@/types/events';
import type { ObjectDef } from '../registry';

const TAB_LABELS: Record<TabKey, string> = {
  home: '首页',
  contacts: '联系人',
  settings: '设置',
};

export const navApi = {
  name: 'nav',
  desc: '切换底部导航的三个页面',
  userDesc: '切换页面（聊天 / 联系人 / 设置）',
  methods: [
    {
      name: 'go',
      desc: '切换到指定页面。用户说「去设置」「打开联系人」「回首页」时用这个。',
      params: [
        {
          name: 'tab',
          type: 'enum' as const,
          values: ['home', 'contacts', 'settings'],
          desc: '目标页面',
          required: true,
        },
      ],
      successText: (args: unknown[]) => `已打开${TAB_LABELS[args[0] as TabKey] ?? String(args[0])}`,
      run: (args: unknown[]) => {
        const tab = args[0] as TabKey;
        uiStore.set({ tab });
        settingsRepo.setApp({ lastTab: tab });
        eventBus.emit('ui:tab-changed', { tab });
        return {};
      },
    },
    {
      name: 'getCurrent',
      kind: 'query',
      desc: '读当前所在的页面。',
      params: [],
      successText: '已读取当前页面',
      run: () => ({ value: uiStore.get().tab }),
    },
  ],
} satisfies ObjectDef;
